import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Loading } from '../components/States';

type Mode = 'signin' | 'signup' | 'magic';

export function LoginPage() {
  const { session, configured, signInWithPassword, signUpWithPassword, sendMagicLink } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const requestedRedirect = (location.state as { from?: string } | null)?.from;
  const redirectTo =
    requestedRedirect?.startsWith('/') && !requestedRedirect.startsWith('//')
      ? requestedRedirect
      : '/thu-vien';

  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session) navigate(redirectTo, { replace: true });
  }, [session, navigate, redirectTo]);

  function selectMode(nextMode: Mode) {
    setMode(nextMode);
    setError(null);
    setNotice(null);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signInWithPassword(email.trim(), password);
      } else if (mode === 'signup') {
        await signUpWithPassword(email.trim(), password, fullName.trim());
        setNotice('Tài khoản đã được tạo. Kiểm tra email nếu Supabase yêu cầu xác nhận.');
      } else {
        await sendMagicLink(email.trim());
        setNotice('Đã gửi liên kết đăng nhập tới email của bạn.');
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Đăng nhập thất bại.');
    } finally {
      setBusy(false);
    }
  }

  if (!configured) {
    return (
      <p className="error">
        Chưa cấu hình Supabase. Điền <code>VITE_SUPABASE_URL</code> và{' '}
        <code>VITE_SUPABASE_ANON_KEY</code> trong <code>web/.env</code> rồi khởi động lại Vite.
      </p>
    );
  }

  if (session) return <Loading label="Đang chuyển tới trang bạn vừa yêu cầu…" />;

  const submitLabel =
    mode === 'signin' ? 'Đăng nhập' : mode === 'signup' ? 'Tạo tài khoản' : 'Gửi liên kết đăng nhập';

  return (
    <section aria-labelledby="auth-title">
      <h1 id="auth-title">
        {mode === 'signup' ? 'Tạo tài khoản' : mode === 'magic' ? 'Đăng nhập bằng email' : 'Đăng nhập'}
      </h1>
      <div className="tabs" role="group" aria-label="Cách đăng nhập">
        <button
          type="button"
          onClick={() => selectMode('signin')}
          aria-pressed={mode === 'signin'}
          aria-current={mode === 'signin'}
          disabled={busy}
        >
          Mật khẩu
        </button>
        <button
          type="button"
          onClick={() => selectMode('signup')}
          aria-pressed={mode === 'signup'}
          aria-current={mode === 'signup'}
          disabled={busy}
        >
          Tạo tài khoản
        </button>
        <button
          type="button"
          onClick={() => selectMode('magic')}
          aria-pressed={mode === 'magic'}
          aria-current={mode === 'magic'}
          disabled={busy}
        >
          Gửi link
        </button>
      </div>

      <form onSubmit={handleSubmit} className="stack-form" aria-busy={busy}>
        {mode === 'signup' && (
          <>
            <label htmlFor="fullName">Họ và tên</label>
            <input
              id="fullName"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              required
              autoComplete="name"
              disabled={busy}
            />
          </>
        )}

        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          disabled={busy}
        />

        {mode !== 'magic' && (
          <>
            <label htmlFor="password">Mật khẩu</label>
            <input
              id="password"
              type="password"
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              disabled={busy}
            />
          </>
        )}

        <button type="submit" disabled={busy}>
          {busy ? 'Đang xử lý…' : submitLabel}
        </button>
      </form>

      {error && <p className="error" role="alert">{error}</p>}
      {notice && <p className="success" role="status" aria-live="polite">{notice}</p>}
    </section>
  );
}
